import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Checkbox from 'components/inputs/checkbox';
import Textarea from 'components/inputs/textarea';
import { ChatbotConversationCategoryType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

export default function ChatbotCategoriesPage() {
  const dispatch = useAppDispatch();
  const { chatbotConversationCategories, chatbotContextOptions } =
    useAppSelector((state) => state.admin);

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [contextOptionIds, setContextOptionIds] = useState<number[]>([]);

  const selectedCategory =
    chatbotConversationCategories.find((c) => c.id === selectedId) ?? null;

  const loadCategory = (category: ChatbotConversationCategoryType | null) => {
    setSystemPrompt(category?.systemPrompt ?? '');
  };

  useEffect(() => {
    loadCategory(selectedCategory);
  }, [selectedId, chatbotConversationCategories]);

  const fetchContextLinks = async (id: number) => {
    try {
      const res = await axios.get(`conversation-category/${id}/context-links`);
      setContextOptionIds(res.data as number[]);
    } catch {
      setContextOptionIds([]);
    }
  };

  const handleSelect = (id: number) => {
    setSelectedId(id);
    setView('detail');
    fetchContextLinks(id);
  };

  const handleBack = () => {
    setView('table');
    setSelectedId(null);
  };

  const handleSave = async () => {
    if (!selectedId) return;

    try {
      await axios.patch('conversation-category', {
        id: selectedId,
        systemPrompt: systemPrompt.trim() || null
      });
      await axios.put(`conversation-category/${selectedId}/context-links`, {
        contextOptionIds
      });
      dispatch(fetchDefaultAdminData());
      successModal('Category updated.');
      setView('table');
      setSelectedId(null);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        loadCategory(selectedCategory);
        if (selectedId) fetchContextLinks(selectedId);
      }
    );
  };

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Categories
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {selectedCategory?.label ?? 'Edit Category'}
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Code</label>
            <div className='rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600'>
              {selectedCategory?.code}
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Label</label>
            <div className='rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600'>
              {selectedCategory?.label}
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Tab Code
            </label>
            <div className='rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600'>
              {selectedCategory?.tabCode ?? '—'}
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-semibold text-gray-700'>
            Auto Provide Context
          </label>
          <p className='text-xs text-gray-500'>
            Select which context sections are automatically included in the
            chatbot system prompt for this category.
          </p>
          <div className='flex flex-col gap-1.5'>
            {chatbotContextOptions.map((option) => (
              <div key={option.id} className='flex items-center gap-2'>
                <Checkbox
                  value={contextOptionIds.includes(option.id)}
                  onChange={(checked) => {
                    if (checked) {
                      setContextOptionIds((prev) => [...prev, option.id]);
                    } else {
                      setContextOptionIds((prev) =>
                        prev.filter((id) => id !== option.id)
                      );
                    }
                  }}
                />
                <span className='text-sm text-gray-700'>{option.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className='flex flex-col gap-1'>
          <label className='text-sm font-semibold text-gray-700'>
            System Prompt
          </label>
          <Textarea
            value={systemPrompt}
            onChange={(v) => setSystemPrompt(v)}
            placeholder='Category-specific system prompt (appended to the chatbot system prompt)'
            rows={16}
          />
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button text='Save Changes' color='green' onClick={handleSave} />
          <Button text='Reset' color='white' onClick={handleReset} />
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Chatbot Categories</div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {['Code', 'Label', 'Tab Code', 'System Prompt'].map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {chatbotConversationCategories.map((cat) => (
              <tr
                key={cat.id}
                onClick={() => handleSelect(cat.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  {cat.code}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {cat.label}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {cat.tabCode ?? '—'}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm'>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      cat.systemPrompt
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {cat.systemPrompt ? 'Configured' : 'None'}
                  </span>
                </td>
              </tr>
            ))}
            {chatbotConversationCategories.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No categories found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {chatbotConversationCategories.length} categor
        {chatbotConversationCategories.length !== 1 ? 'ies' : 'y'}
      </div>
    </div>
  );
}
